import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { activeStylesContinue, font } from "../../configs";
import { GENDERS, getFileFromCloud } from "../../constants/modal";

import { Help as HelpIcon } from "@mui/icons-material";
import { Popper, Typography, Paper, ClickAwayListener } from "@mui/material";
import { capitalizeFirstLetter, useIsMobile } from "../../utils";
import { handleAnalytics } from "../../utils/tracking";
import { hoverStylesContinue } from "../../configs/stylesLoader";
import { ArrowForward as ChevronRight } from "@mui/icons-material";
import { findBrandByDomain } from "../../configs/configLoader";
import { isFootwear } from "../../utils/categoryParams";

import "./index.css";

interface IPropsQrcodeMobile {
  loading?: boolean;
  url?: string;
  gender: string | undefined;
  descriptionStyles: React.CSSProperties;
}

const QrcodeMobile: FC<IPropsQrcodeMobile> = ({
  loading,
  url,
  gender,
  descriptionStyles,
}) => {
  const isMobile = useIsMobile();

  const { t } = useTranslation("components/qrcode/mobile");


  const scanType = isFootwear ? "shoe_scan" : "scan";

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [hovered, setHovered] = useState(false);

  const brandDefined = findBrandByDomain();

  const isPeserico = brandDefined?.name === "Peserico";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isIntersport = brandDefined?.name === "Intersport";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isTDF = brandDefined?.name === "TDF";

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "info-popper" : undefined;

  const openScan = async () => {
    if (!loading) {
      handleAnalytics("action", "scan_clicked", null);

      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const CTAStyles: React.CSSProperties = {
    ...activeStylesContinue,
    ...(hovered ? hoverStylesContinue : {}),
    textTransform:
      activeStylesContinue.textTransform === "capitalize"
        ? "none"
        : activeStylesContinue.textTransform,
    maxHeight: isTDF ? "28px" : isGivenchy ? "40px" : "fit-content",
    height: isTDF ? "28px" : isVB ? "30px" : "auto",
    padding: isTDF ? "0" : isPeserico ? "12px 16px" : isVB ? "7px" : "16px",
  };

  const arrowContainerStyle: React.CSSProperties = {
    display: "inline-block",
    width: hovered ? 16 : 0,
    overflow: "hidden",
    verticalAlign: "middle",
    transition: "width 160ms ease",
  };

  const iconStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    opacity: hovered ? 1 : 0,
    transform: hovered ? "translateX(0)" : "translateX(-6px)",
    transition: "opacity 160ms ease, transform 160ms ease",
    display: "inline-block",
    verticalAlign: "middle",
    pointerEvents: "none",
  };

  return (
    <div className="mobile-qr-container">
      <video muted className="qrcode-video" playsInline autoPlay loop>
        <source
          src={
            isFootwear
              ? "https://kleep-prod-assets-public.s3.eu-west-1.amazonaws.com/videos/Animation_Footwear.mp4"
              : gender === GENDERS.M
                ? getFileFromCloud("assets/qrcode/demo-male.mp4")
                : getFileFromCloud("assets/qrcode/demo-female.mp4")
          }
          type="video/mp4"
        />
      </video>
      <div className="help-info">
        {!isGivenchy ? <HelpIcon /> : null}
        <span
          tabIndex={0}
          className="link"
          aria-describedby={id}
          onClick={handleClick}
          onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
          onMouseLeave={() => setAnchorEl(null)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              setAnchorEl(anchorEl ? null : e.currentTarget);
              e.preventDefault();
            }
          }}
          style={{
            color: `${isGivenchy ? "#000000" : descriptionStyles.color}`,
            fontFamily: `${font}, sans-serif`,
          }}
        >
          {t("info")}
        </span>
        <Popper id={id} open={open} anchorEl={anchorEl} placement="top">
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Paper
              className="popper-container"
              style={{
                border: `1px solid ${isGivenchy ? "#000000" : descriptionStyles.color}`,
                fontFamily: `${font}, sans-serif`,
              }}
            >
              <Typography
                style={{
                  color: `${isGivenchy ? "#000000" : descriptionStyles.color}`,
                  fontFamily: `${font}, sans-serif`,
                }}
              >
                {t("popupText")}
              </Typography>
              <div
                className="popper-triangle"
                style={{
                  borderTop: `10px solid ${isGivenchy ? "#000000" : descriptionStyles.color}`,
                }}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>
      </div>
      <button
        tabIndex={0}
        type="button"
        className={`continue-button`}
        onMouseEnter={(e) => {
          if (isMobile) return;
          setHovered(true);
          Object.assign(e.currentTarget.style, hoverStylesContinue);
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          Object.assign(e.currentTarget.style, CTAStyles);
        }}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={openScan}
        style={CTAStyles}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
          {activeStylesContinue.textTransform === "capitalize"
            ? capitalizeFirstLetter(t(scanType))
            : t(scanType)}

          {isIntersport && (
            <span style={arrowContainerStyle} aria-hidden>
              <ChevronRight style={iconStyle} />
            </span>
          )}
        </div>
      </button>
    </div>
  );
};

export default QrcodeMobile;
